import React, { useEffect, useMemo, useState } from "react";
import { api, API, tokenStore } from "../lib/api";
import { toast } from "sonner";
import { DropZone } from "./DropZone";
import { ClipboardList, FileText } from "lucide-react";

const DAY_SHORT = ["Du", "Lu", "Ma", "Mi", "Jo", "Vi", "Sâ"];
const fmtD = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const fmtNum = (n) => parseFloat((Number(n) || 0).toFixed(2));
const mondayOf = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  const wd = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - wd);
  return d.toISOString().slice(0, 10);
};
const addDaysIso = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const siteOf = (e) =>
  e.kind === "magazie" ? "🏪 Magazie" : e.kind === "birou" ? "🏢 Birou" : (e.project_label || e.label || "—");

export const PersonMonthWorkLog = ({ personId, month, entries }) => {
  const [reports, setReports] = useState([]);
  const [uploadingWk, setUploadingWk] = useState(null);
  const [expandedWks, setExpandedWks] = useState({});
  const toggleWk = (wk) => setExpandedWks((p) => ({ ...p, [wk]: !p[wk] }));

  const uploadForWeek = async (w, file) => {
    if (!file) return;
    setUploadingWk(w.wk);
    try {
      const wkEntries = (entries || []).filter((e) => e.date >= w.wk && e.date <= addDaysIso(w.wk, 6) && (e.hours || 0) > 0);
      const regieIds = [...new Set(wkEntries.map((e) => e.regie_id).filter(Boolean))];
      const projIds = [...new Set(wkEntries.map((e) => e.project_id).filter(Boolean))];
      const fd = new FormData();
      fd.append("file", file);
      fd.append("report_type", "wochen");
      fd.append("start_date", w.wk);
      fd.append("end_date", addDaysIso(w.wk, 6));
      fd.append("person_id", personId);
      if (regieIds.length === 1) fd.append("regie_id", regieIds[0]);
      if (projIds.length === 1) fd.append("project_id", projIds[0]);
      fd.append("note", "Încărcat din Calendar Ore (profil persoană)");
      const { data } = await api.post("/signed-reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReports((prev) => [...prev, data]);
      toast.success(`Raport încărcat${data.auto_closed_tasks ? ` · ${data.auto_closed_tasks} task(uri) „raport lipsă" închise automat` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la încărcarea raportului");
    } finally { setUploadingWk(null); }
  };

  useEffect(() => {
    api.get(`/signed-reports?worked_by_person_id=${personId}&month=${month}`)
      .then((r) => setReports(r.data || []))
      .catch(() => setReports([]));
  }, [personId, month]);

  const weeks = useMemo(() => {
    const worked = (entries || []).filter((e) => (e.hours || 0) > 0 || (e.hours_driven || 0) > 0);
    const byWeek = {};
    for (const e of worked) {
      const wk = mondayOf(e.date);
      (byWeek[wk] = byWeek[wk] || []).push(e);
    }
    return Object.entries(byWeek)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([wk, list]) => {
        const sites = [...new Set(list.map(siteOf))];
        const byDate = {};
        for (const e of list) (byDate[e.date] = byDate[e.date] || []).push(e);
        const days = Object.entries(byDate).sort((a, b) => a[0].localeCompare(b[0]));
        return {
          wk,
          days,
          sites,
          totalH: fmtNum(list.reduce((s, e) => s + (e.hours || 0), 0)),
          totalDriven: fmtNum(list.reduce((s, e) => s + (e.hours_driven || 0), 0)),
        };
      });
  }, [entries]);

  const repUrl = (rid) => `${API}/signed-reports/${rid}/file?token=${encodeURIComponent(tokenStore.get() || "")}`;

  if (weeks.length === 0 && reports.length === 0) return null;

  return (
    <div className="mt-8 border-t border-[var(--border)] pt-6" data-testid="month-worklog-section">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList size={18} className="text-[var(--primary)]" />
        <h3 className="font-display text-lg font-bold">Zile lucrate — detaliat pe săptămâni</h3>
      </div>

      <div className="space-y-4">
        {weeks.map((w) => {
          const wkEnd = addDaysIso(w.wk, 6);
          const weekReports = reports.filter((r) => r.start_date <= wkEnd && r.end_date >= w.wk);
          const expanded = !!expandedWks[w.wk];
          return (
          <div key={w.wk} className="border border-zinc-200 rounded-lg overflow-hidden bg-white" data-testid={`worklog-week-${w.wk}`}>
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-xs font-bold text-zinc-500 uppercase tracking-wide">Săpt. {fmtD(w.wk)} – {fmtD(addDaysIso(w.wk, 6))}</span>
              <button
                type="button"
                onClick={() => toggleWk(w.wk)}
                className={`font-extrabold hover:underline ${w.sites.length === 1 ? "text-sm text-[var(--primary)]" : "text-xs text-zinc-600"}`}
                data-testid={`worklog-week-site-${w.wk}`}
                title={weekReports.length > 0 ? "Click → deschide/închide raportul mare dedesubt" : "Niciun raport încărcat pentru această săptămână"}
              >
                🏗 {w.sites.length === 1 ? w.sites[0] : `${w.sites.length} șantiere`}
                {weekReports.length > 0 && <span className="ml-1 text-[10px] font-bold text-emerald-700">📄 {expanded ? "▲" : "▼"}</span>}
              </button>
              <span className="ml-auto text-sm font-bold tabular-nums">Σ {w.totalH}h{w.totalDriven > 0 ? <span className="text-amber-700 font-semibold text-xs"> · 🚗 {w.totalDriven}h</span> : null}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {w.days.map(([date, list]) => {
                  const dayH = fmtNum(list.reduce((s, e) => s + (e.hours || 0), 0));
                  const dayDr = fmtNum(list.reduce((s, e) => s + (e.hours_driven || 0), 0));
                  return (
                    <tr key={date} className="border-b border-zinc-100 last:border-0" data-testid={`worklog-day-${date}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap w-24">
                        <span className="font-bold">{DAY_SHORT[new Date(date + "T00:00:00Z").getUTCDay()]}</span>{" "}
                        <span className="text-zinc-500 tabular-nums">{fmtD(date)}</span>
                      </td>
                      <td className="px-2 py-1.5">
                        {list.map((e, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0" style={{ background: e.color || "#0044FF" }} />
                            <span className="font-semibold text-zinc-800">{siteOf(e)}</span>
                            {list.length > 1 && <span className="text-xs text-zinc-500 tabular-nums">({fmtNum(e.hours)}h)</span>}
                            {e.note && <span className="text-xs text-zinc-500 italic truncate">— {e.note}</span>}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap w-28">
                        <span className="font-bold text-[var(--primary)] tabular-nums">{dayH}h</span>
                        {dayDr > 0 && <span className="text-xs text-amber-700 font-semibold"> · 🚗 {dayDr}h</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {weekReports.length > 0 ? (
              <DropZone onFiles={(files) => uploadForWeek(w, files[0])} disabled={uploadingWk === w.wk} className="px-3 py-2.5 bg-emerald-50/50 border-t border-emerald-200" testId={`worklog-week-reports-${w.wk}`}>
                <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wide mb-2">📄 Raport fizic încărcat ({weekReports.length}) — {expanded ? "click pe titlul șantierului ca să îl micșorezi" : "click pe titlul șantierului sau ține mouse-ul pe imagine ca să o mărești"}</div>
                <div className={expanded ? "flex flex-col gap-4" : "flex flex-wrap items-start gap-3"}>
                  {weekReports.map((r) => {
                    const isImg = (r.content_type || "").startsWith("image/");
                    const site = r.regie_name || r.project_name || "—";
                    const range = r.start_date === r.end_date ? fmtD(r.start_date) : `${fmtD(r.start_date)} – ${fmtD(r.end_date)}`;
                    return (
                      <a
                        key={r.id}
                        href={repUrl(r.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="group block border border-zinc-300 rounded-lg overflow-hidden bg-white hover:shadow-xl hover:border-emerald-400 transition-shadow"
                        data-testid={`week-report-${w.wk}-${r.id}`}
                        title="Ține mouse-ul pentru mărire · click = deschide în tab nou"
                      >
                        {isImg ? (
                          <img
                            src={repUrl(r.id)}
                            alt={r.original_filename || "raport"}
                            className={expanded
                              ? "w-full max-h-[820px] object-contain bg-zinc-100"
                              : "h-24 group-hover:h-[560px] w-auto max-w-full object-contain bg-zinc-100 transition-[height] duration-300 ease-out"}
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-24 w-32 flex flex-col items-center justify-center bg-red-50 text-red-600 gap-1">
                            <FileText size={24} />
                            <span className="text-[10px] font-bold">PDF — deschide</span>
                          </div>
                        )}
                        <div className="px-2 py-1.5 border-t border-zinc-200">
                          <span className="text-xs font-bold">🏗 {site}</span>
                          <span className="text-[11px] text-zinc-500 tabular-nums"> · {range}</span>
                          {r.person_name && <span className="text-[10px] text-zinc-400"> · pt. {r.person_name}</span>}
                        </div>
                      </a>
                    );
                  })}
                </div>
                <label className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md cursor-pointer bg-zinc-100 text-zinc-700 border border-zinc-300 hover:bg-zinc-200 transition-colors" data-testid={`worklog-upload-report-${w.wk}`}>
                  📷 {uploadingWk === w.wk ? "Se încarcă…" : "Adaugă alt raport"} <span className="font-normal text-zinc-500">· sau trage fișierul aici</span>
                  <input type="file" accept="image/*,.pdf" className="hidden" disabled={uploadingWk === w.wk} onChange={(e) => { uploadForWeek(w, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </DropZone>
            ) : (
              <DropZone onFiles={(files) => uploadForWeek(w, files[0])} disabled={uploadingWk === w.wk} className="px-3 py-2 bg-amber-50 border-t border-amber-200 flex flex-wrap items-center gap-3" testId={`worklog-week-noreport-${w.wk}`}>
                <span className="text-[11px] font-semibold text-amber-800">⚠ Niciun raport fizic încărcat pentru această săptămână</span>
                <label className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md cursor-pointer bg-amber-600 text-white hover:bg-amber-700 transition-colors" data-testid={`worklog-upload-report-${w.wk}`}>
                  📷 {uploadingWk === w.wk ? "Se încarcă…" : "Încarcă raportul acum"}
                  <input type="file" accept="image/*,.pdf" className="hidden" disabled={uploadingWk === w.wk} onChange={(e) => { uploadForWeek(w, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                <span className="text-[10px] text-zinc-500">sau trage fișierul aici</span>
              </DropZone>
            )}
          </div>
          );
        })}
        {weeks.length === 0 && <div className="text-sm text-zinc-400 italic">Nicio zi lucrată în luna selectată.</div>}
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={16} className="text-emerald-700" />
          <h4 className="font-bold text-sm">Rapoarte de lucru atașate în lună ({reports.length})</h4>
        </div>
        {reports.length === 0 ? (
          <div className="text-sm text-zinc-400 italic">Niciun raport atașat pentru luna selectată.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {reports.map((r) => {
              const isImg = (r.content_type || "").startsWith("image/");
              const site = r.regie_name || r.project_name || "—";
              const range = r.start_date === r.end_date ? fmtD(r.start_date) : `${fmtD(r.start_date)} – ${fmtD(r.end_date)}`;
              return (
                <a
                  key={r.id}
                  href={repUrl(r.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="block border border-zinc-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                  data-testid={`worklog-report-${r.id}`}
                >
                  {isImg ? (
                    <img src={repUrl(r.id)} alt={r.original_filename || "raport"} className="w-full h-40 object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-40 flex flex-col items-center justify-center bg-red-50 text-red-600 gap-1">
                      <FileText size={30} />
                      <span className="text-xs font-bold">PDF</span>
                    </div>
                  )}
                  <div className="px-2 py-1.5">
                    <div className="text-xs font-bold truncate">🏗 {site}</div>
                    <div className="text-[11px] text-zinc-500 tabular-nums">{range}{r.report_type === "tages" ? " · zilnic" : r.report_type === "wochen" ? " · săptămânal" : ""}</div>
                    {r.person_name && <div className="text-[10px] text-zinc-400 truncate">încărcat pt. {r.person_name}</div>}
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
